import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import type { ColumnFiltersState, OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { ArrowLeftRight, Download, Upload } from 'lucide-react';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import { DataTable } from 'shared/ui/data-table/DataTable';
import { formatPropertyPrice } from 'shared/utils/formatPropertyPrice';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';

import { useGetProjectsQuery } from '../api/projects';
import {
  useGetPropertiesBySubprojectQuery,
  useGetPropertyStatsBySubprojectQuery,
  useLazyExportPropertiesQuery,
} from '../api/properties';
import type { Project } from '../model/project';
import PropertyUploadDialog from './propiedades/PropertyUploadDialog';
import StockEquivalencesDialog from './propiedades/StockEquivalencesDialog';
import { buildPropertyColumns } from './propiedades/columns';

const findSubProject = (projects: Project[], masterProjectId: number, subProjectId: number): Project | null => {
  const masterProject = projects.find((project) => project.id === masterProjectId && project.masterProjectId === null);
  const nestedSubProject = masterProject?.subProjects?.find((project) => project.id === subProjectId) ?? null;
  if (nestedSubProject) return nestedSubProject;

  return projects.find((project) => project.id === subProjectId && project.masterProjectId === masterProjectId) ?? null;
};

const getBadgeVariant = (available: number, count: number): 'default' | 'secondary' | 'destructive' => {
  if (available === 0) return 'secondary';
  const ratio = count > 0 ? available / count : 0;
  return ratio >= 0.6 ? 'default' : 'destructive';
};

interface LegendRowProps {
  color: string;
  label: string;
  count: number;
  total: number;
}

const LegendRow = ({ color, label, count, total }: LegendRowProps) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className={`size-2.5 rounded-full ${color}`} />
        <span className="text-sm text-text-default">{label}</span>
      </div>
      <span className="text-sm font-semibold text-text-subtle">
        {count} ·{pct}%
      </span>
    </div>
  );
};

const SubprojectPropiedades = () => {
  const { masterProjectId, subProjectId } = useParams();
  const numericMasterProjectId = Number(masterProjectId);
  const numericSubProjectId = Number(subProjectId);
  const { data: projects = [] } = useGetProjectsQuery();
  const project =
    Number.isFinite(numericMasterProjectId) && Number.isFinite(numericSubProjectId)
      ? findSubProject(projects, numericMasterProjectId, numericSubProjectId)
      : null;

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);
  const [appliedColumnFilters, setAppliedColumnFilters] = useState(debouncedColumnFilters);
  if (appliedColumnFilters !== debouncedColumnFilters) {
    setAppliedColumnFilters(debouncedColumnFilters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  const subprojectId = project?.id ?? 0;

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { data, isLoading } = useGetPropertiesBySubprojectQuery(
    {
      subprojectId,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      filters: appliedColumnFilters,
      sorting,
    },
    { skip: !project }
  );
  const { data: stats, isLoading: isLoadingStats } = useGetPropertyStatsBySubprojectQuery(
    { subprojectId },
    { skip: !project }
  );

  const [triggerExport, { isFetching: isExporting }] = useLazyExportPropertiesQuery();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [equivalencesOpen, setEquivalencesOpen] = useState(false);

  const masterProjectIdValue = project?.masterProjectId ?? null;
  const masterProject = useMemo(() => {
    if (masterProjectIdValue === null) return null;
    return projects.find((p) => p.id === masterProjectIdValue) ?? null;
  }, [projects, masterProjectIdValue]);

  const handleExport = async (): Promise<void> => {
    if (!project || project.masterProjectId === null) return;
    try {
      const blob = await triggerExport({
        projectId: project.masterProjectId,
        subprojectId: project.id,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `propiedades-${project.name.replace(/\s+/g, '_')}-${date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Export errors are surfaced by the disabled/loading state only in this view.
    }
  };

  const properties = useMemo(() => data?.properties ?? [], [data]);

  const columns = useMemo(
    () => buildPropertyColumns({ types: stats?.types ?? [], orientations: stats?.orientations ?? [] }),
    [stats?.types, stats?.orientations]
  );

  const total = stats?.total ?? 0;
  const available = stats?.available ?? 0;
  const unavailable = stats?.unavailable ?? 0;
  const typologies = stats?.typologies ?? [];
  const maxCount = typologies.reduce((acc, t) => Math.max(acc, t.count), 0);

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <p className="text-text-muted text-sm">
          Selecciona un subproyecto en el sidebar para gestionar sus propiedades.
        </p>
      </div>
    );
  }

  const availablePct = total > 0 ? (available / total) * 100 : 0;

  return (
    <div className="flex flex-col gap-8 min-w-0">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-vivid mb-1">Inventario Operacional</p>
          <h1 className="text-3xl font-extrabold font-display text-brand-navy">Propiedades Cargadas</h1>
          <p className="text-sm text-text-subtle mt-1">
            Revisa disponibilidad, detecta datos incompletos y exporta el inventario · {project.name}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2">
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            disabled={!masterProject}
            className="bg-brand-vivid text-white hover:bg-brand-sky-dark"
          >
            <Upload className="size-4" />
            Cargar propiedades
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEquivalencesOpen(true)} disabled={!masterProject}>
            <ArrowLeftRight className="size-4" />
            Cargar equivalencias
          </Button>
        </div>
      </div>

      {/* Estado + Total */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-brand-navy">Estado del Inventario</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-border-subtle">
              <div className="absolute inset-y-0 left-0 bg-success" style={{ width: `${availablePct}%` }} />
              <div
                className="absolute inset-y-0 bg-error"
                style={{ left: `${availablePct}%`, width: `${100 - availablePct}%` }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <LegendRow color="bg-success" label="Disponibles" count={available} total={total} />
              <LegendRow color="bg-error" label="No disponibles" count={unavailable} total={total} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-text-subtle">
              Total propiedades
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold font-display text-brand-navy">
              {isLoadingStats ? <Skeleton className="h-10 w-16" /> : total}
            </p>
            <p className="text-xs text-text-subtle mt-1">{available} disponibles</p>
          </CardContent>
        </Card>
      </div>

      {/* Unidades disponibles */}
      <div className="flex flex-col gap-4">
        <h2 className="text-base font-bold font-display text-brand-navy">Unidades disponibles</h2>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-4 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-brand-navy">Precios mínimos por tipología</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col">
              {isLoadingStats && <Skeleton className="h-24 w-full" />}
              {!isLoadingStats && typologies.length === 0 && (
                <p className="text-sm text-text-subtle">Sin datos de precios.</p>
              )}
              {typologies.slice(0, 5).map((t) => (
                <div
                  key={t.key}
                  className="flex items-center justify-between py-2 border-b border-border-subtle last:border-b-0"
                >
                  <span className="text-sm font-semibold text-text-default">{t.label}</span>
                  <span className="text-sm text-text-subtle">
                    {t.minPrice !== null ? `Desde ${formatPropertyPrice(t.minPrice, t.currency)}` : '—'}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="min-w-0">
            {isLoadingStats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[0, 1].map((i) => (
                  <Skeleton key={i} className="h-36 rounded-xl" />
                ))}
              </div>
            )}
            {!isLoadingStats && typologies.length === 0 && (
              <p className="text-sm text-text-subtle">Aún no hay propiedades cargadas para este subproyecto.</p>
            )}
            {!isLoadingStats && typologies.length > 0 && (
              <Carousel opts={{ align: 'start', containScroll: 'trimSnaps' }} className="w-full px-10">
                <CarouselContent className="-ml-4">
                  {typologies.map((t) => {
                    const fillPct = maxCount > 0 ? (t.count / maxCount) * 100 : 0;
                    const badgeVariant = getBadgeVariant(t.available, t.count);
                    return (
                      <CarouselItem key={t.key} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 py-1">
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center justify-between font-display text-brand-navy">
                              <span className="text-lg font-extrabold">{t.label}</span>
                              <Badge variant={badgeVariant}>{t.available} disp.</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="flex flex-col gap-2">
                            <p className="text-base font-bold text-brand-vivid">
                              {t.minPrice !== null
                                ? `Desde ${formatPropertyPrice(t.minPrice, t.currency)}`
                                : 'Sin precio'}
                            </p>
                            <p className="text-xs text-text-subtle">
                              {t.propertyTypeLabel}
                              {t.avgInteriorSize !== null && ` · ${Math.round(t.avgInteriorSize)} m² promedio`}
                            </p>
                            <Progress value={fillPct} className="h-1" />
                          </CardContent>
                        </Card>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                {typologies.length > 3 && (
                  <>
                    <CarouselPrevious className="left-1" />
                    <CarouselNext className="right-1" />
                  </>
                )}
              </Carousel>
            )}
          </div>
        </div>
      </div>

      {/* Detalle de unidades */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold font-display text-brand-navy">Detalle de unidades</h2>
            <p className="text-sm text-text-subtle">Listado completo de propiedades cargadas en este subproyecto.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void handleExport();
            }}
            disabled={isExporting || total === 0}
          >
            <Download className="size-4" />
            {isExporting ? 'Exportando…' : 'Exportar Excel'}
          </Button>
        </div>
        {isLoading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : (
          <DataTable
            columns={columns}
            data={properties}
            serverSide={{
              rowCount: data?.total ?? 0,
              pagination,
              onPaginationChange: setPagination,
              sorting,
              onSortingChange: handleSortingChange,
              columnFilters,
              onColumnFiltersChange: setColumnFilters,
            }}
          />
        )}
      </div>

      {masterProject && (
        <PropertyUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          masterProject={{ id: masterProject.id, name: masterProject.name }}
          subproject={{ id: project.id, name: project.name }}
        />
      )}
      {masterProject && (
        <StockEquivalencesDialog
          open={equivalencesOpen}
          onOpenChange={setEquivalencesOpen}
          masterProject={{ id: masterProject.id, name: masterProject.name }}
        />
      )}
    </div>
  );
};

const PropiedadesView = () => {
  const { subProjectId } = useParams();
  return <SubprojectPropiedades key={subProjectId} />;
};

export default PropiedadesView;
